/**
 * Workflow Validation V.2 (CJXPM-2914) — error ที่ BE ตอบกลับตอน generate สายอนุมัติ
 *
 * ตาม API Spec (Coda `API Spec` → `API response workflow v2`) BE ยุบทุกเคสของ workflow validation
 * มาไว้ที่ code เดียวคือ 00201 แล้วส่งข้อความไทยสำเร็จรูปมาใน `messageTh` (มี <br/> และ <b> มาในตัว
 * รวมถึงชื่อร้าน/ชื่อขั้นตอนที่แทรกและ join ให้แล้ว)
 * error code อื่นนอกจากนี้ BE ระบุให้ตีเป็น Generic Error
 */
export const STORE_EXPENSE_WORKFLOW_ERROR_CODE = '00201';

/** เคส data changed by another user — validation เดิม ไม่ได้อยู่ในชุด workflow V.2 */
export const STORE_EXPENSE_DATA_CHANGED_ERROR_CODE = '00003';

/** สถานะของเอกสารไม่รองรับการบันทึกหลักฐาน — validation เดิม */
export const STORE_EXPENSE_INVALID_STATUS_ERROR_CODE = '00001';

/**
 * บรรทัดแรกของ Failed modal ตาม AC (deck slide 5) — `messageTh` ของ BE เริ่มที่บรรทัดที่สอง
 * ทั้งที่ `message`/`messageEn` มี "Unable to create the workflow.<br/>" นำหน้าครบทุกเคส
 * CJXPM-3032: QA ทักว่าข้อความขาดบรรทัดนี้ไป FE จึงเติมกลับให้ตรง AC
 * ถ้าวันหลัง BE เติมมาใน `messageTh` เอง `getStoreExpenseWorkflowErrorMessage()` จะไม่เติมซ้ำ
 */
export const STORE_EXPENSE_WORKFLOW_ERROR_HEADLINE = 'ไม่สามารถสร้างสายอนุมัติได้';

export interface StoreExpenseSubmitError {
  code: string;
  message?: string;
  messageEn?: string;
  messageTh?: string;
}

/**
 * เคส "ต้องใช้ store แต่ไม่ส่งมา" BE ตั้งใจไม่ทำข้อความไทยไว้ (ยืนยันแล้ว 2026-09-03) และ AC ระบุว่า
 * ไม่เกิดกับ Store Expense — ถ้ามาจริงจะไม่มีอะไรให้แสดง จึงต้องตกไปใช้ Generic Error แทน
 * มิฉะนั้น Failed modal จะเปิดขึ้นมาโดยไม่มีข้อความเลย
 */
export function hasStoreExpenseWorkflowErrorMessage(error: StoreExpenseSubmitError): boolean {
  return !!error?.messageTh?.trim();
}

/**
 * บาง `messageTh` ของ BE มีช่องว่างค้างหลัง `<br/>` (เช่นเคสร้านไม่อยู่ในไฟล์ import:
 * `...สำหรับร้าน<br/> <b>“10001”</b>`) ทำให้บรรทัดถัดไปเยื้องไปหนึ่งช่องใน modal ที่จัดกึ่งกลาง
 */
function trimLineLeadingSpaces(message: string): string {
  return message.replace(/(<br\s*\/?>)\s+/g, '$1');
}

/** ประกอบข้อความ Failed modal = บรรทัดแรกตาม AC + `messageTh` ที่ BE ประกอบมาให้แล้ว */
export function getStoreExpenseWorkflowErrorMessage(error: StoreExpenseSubmitError): string {
  const detail = trimLineLeadingSpaces(error?.messageTh?.trim() ?? '');

  if (!detail) {
    return STORE_EXPENSE_WORKFLOW_ERROR_HEADLINE;
  }

  return detail.startsWith(STORE_EXPENSE_WORKFLOW_ERROR_HEADLINE)
    ? detail
    : `${STORE_EXPENSE_WORKFLOW_ERROR_HEADLINE}<br/>${detail}`;
}

/**
 * ข้อความของ Failed modal สำหรับ validation เดิม (คนละชุดกับ workflow V.2 ที่ BE ส่ง `messageTh` มาให้)
 *
 * `00001` ตาม API Spec (`API Spec` → `Submit Store Expense Request` → `Error Validation`) คือเคส
 * "submit data ที่ status ไม่ถูกต้อง แต่ version ตรงกัน" (`Invalid status for submitting an evidence
 * store expense request.`) — เอกสารถูกดำเนินการไปแล้วระหว่างที่ผู้ใช้ค้างหน้าอยู่ ทั้งแอปตีเคสนี้เป็น
 * "ข้อมูลเปลี่ยนไปแล้ว ให้ตรวจสอบใหม่" อยู่ก่อนแล้ว (`layout.effects.ts` ส่งทั้ง `00001` และ `00003`
 * เข้า `LayoutActionVersionError` ซึ่งแสดง `SIDEBAR.CONTENT.ERROR`) จึงชี้มาที่ข้อความกลางตัวเดียวกัน
 * แทนการตั้ง key ใหม่ใน `STORE_EXPENSE.ERROR_CODE` — ถ้า PO กำหนดข้อความเฉพาะของเคสนี้มา ให้แก้ที่นี่จุดเดียว
 */
const STORE_EXPENSE_ERROR_MESSAGE_KEY: Record<string, string> = {
  [STORE_EXPENSE_DATA_CHANGED_ERROR_CODE]: `STORE_EXPENSE.ERROR_CODE.${STORE_EXPENSE_DATA_CHANGED_ERROR_CODE}`,
  [STORE_EXPENSE_INVALID_STATUS_ERROR_CODE]: 'SIDEBAR.CONTENT.ERROR'
};

/** translation key ของ Failed modal สำหรับ error code ที่ไม่ใช่ workflow V.2 */
export function getStoreExpenseErrorMessageKey(code: string): string {
  return STORE_EXPENSE_ERROR_MESSAGE_KEY[code];
}
